import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send } from 'lucide-react';
import { Captcha } from './Captcha.tsx';
import { projectId, publicAnonKey } from '../../utils/supabase/info.tsx';
import { SuccessMessage } from './SuccessMessage.tsx';
import styles from '../contact/ContactForm.module.css';

export function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [captchaResetTrigger, setCaptchaResetTrigger] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isCaptchaValid) {
      alert('Please complete the CAPTCHA verification');
      return;
    }
    
    setIsSubmitting(true);
    setErrorMessage('');
    
    
    try {
      // Send form data to backend
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-db5c36e4/contact`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${publicAnonKey}`
          },
          body: JSON.stringify(formData)
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        console.error('Form submission error:', result.error);
        setErrorMessage(result.error || 'Failed to send message. Please try again.');
        setIsSubmitting(false);
        return;
      }

      console.log('Form submitted successfully:', result.message);
      setIsSubmitting(false);
      setIsSubmitted(true);
      setShowSuccessModal(true);
      
      // Reset form after showing modal
      setTimeout(() => {
        setFormData({ name: '', email: '', company: '', message: '' });
        setIsSubmitted(false);
        setIsCaptchaValid(false);
        setCaptchaResetTrigger(!captchaResetTrigger);
        setErrorMessage('');
      }, 500);

    } catch (error) {
      console.error('Network error during form submission:', error);
      setErrorMessage('Network error. Please check your connection and try again.');
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const requiredFieldsFilled = formData.name && formData.email && formData.message;

  return (
    <motion.div
      className={styles.formContainer}
      whileHover={{ boxShadow: '0 20px 60px rgba(255, 154, 86, 0.12)' }}
    >
      <div className={styles.formHeader}>
        <h3 className={styles.formTitle}>Send us a message</h3>
        <p className={styles.formSubtitle}>We'll get back to you within 24 hours</p>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label htmlFor="name" className={styles.label}>Your Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={styles.input}
            placeholder="John Doe"
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="email" className={styles.label}>Email Address</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={styles.input}
            placeholder="john@company.com"
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="company" className={styles.label}>Company (Optional)</label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            className={styles.input}
            placeholder="Your Company Inc."
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="message" className={styles.label}>Your Message</label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            className={styles.textarea}
            placeholder="Tell us about your project or question..."
            rows={5}
            required
          />
        </div>

        <Captcha 
          onVerify={setIsCaptchaValid} 
          resetTrigger={captchaResetTrigger}
          requiredFieldsFilled={requiredFieldsFilled}
        />

        {errorMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={styles.errorMessage}
          >
            {errorMessage}
          </motion.div>
        )}

        <motion.button
          type="submit"
          className={styles.submitButton}
          disabled={isSubmitting || isSubmitted || !isCaptchaValid}
          whileHover={{ scale: isSubmitting || isSubmitted || !isCaptchaValid ? 1 : 1.02 }}
          whileTap={{ scale: isSubmitting || isSubmitted || !isCaptchaValid ? 1 : 0.98 }}
        >
          {isSubmitting ? (
            <>
              <motion.div
                className={styles.spinner}
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
              Sending...
            </>
          ) : isSubmitted ? (
            <>
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                ✓
              </motion.span>
              Message Sent!
            </>
          ) : (
            <>
              Send Message
              <Send size={18} />
            </>
          )}
        </motion.button>
      </form>

      <AnimatePresence>
        {showSuccessModal && (
          <SuccessMessage onClose={handleCloseModal} />
        )}
      </AnimatePresence>
    </motion.div>
  );
}